import { HISTORY_ACTION_MODE, NodeId } from '@candulabs/craft';
import invariant from 'invariant';
import { useMemo } from 'react';

import { useMultiCssSelector } from 'craft/hooks/useMultiCssSelector';
import { useIsUsingDefaultCssControls, useViewportBreakpoint } from 'craft/state/viewport/selectors';
import { CssSelectorId, getResponsiveSelectorIds, isBaseSelectorId, MediaBreakpoint } from 'craft/types';

import { useRootCss } from './useRootCss';

interface Options {
  cssSelectorId?: CssSelectorId;
  commonProperty?: string;
}

export const useResponsiveCss = (
  nodeIds: NodeId[],
  { commonProperty, cssSelectorId = CssSelectorId.Root }: Options,
) => {
  invariant(
    isBaseSelectorId(cssSelectorId),
    "(useResponsiveCss) You're trying to use a responsive hook with a non base css selector",
  );
  const isUsingDefaultCssControls = useIsUsingDefaultCssControls();
  const viewportBreakpoint = useViewportBreakpoint();
  const viewportSelectors = getResponsiveSelectorIds(cssSelectorId);
  const { css, setCss } = useRootCss(nodeIds, commonProperty, cssSelectorId);
  const { css: mdCss, setCssSelector: setMdCssSelector } = useMultiCssSelector(
    nodeIds,
    viewportSelectors[MediaBreakpoint.MD],
    commonProperty,
  );
  const { css: smCss, setCssSelector: setSmCssSelector } = useMultiCssSelector(
    nodeIds,
    viewportSelectors[MediaBreakpoint.SM],
    commonProperty,
  );

  const responsiveCss = useMemo(() => {
    const safeCss = css ?? {};
    if (viewportBreakpoint === MediaBreakpoint.LG) {
      return safeCss;
    }
    if (viewportBreakpoint === MediaBreakpoint.MD) {
      return { ...safeCss, ...mdCss };
    }
    return { ...safeCss, ...mdCss, ...smCss };
  }, [css, mdCss, smCss, viewportBreakpoint]);

  const setResponsiveCss = (
    newCss: object,
    historyMode?: HISTORY_ACTION_MODE,
    throttleNumber?: number,
  ) => {
    if (isUsingDefaultCssControls) {
      setCss(newCss, historyMode, throttleNumber);
    } else if (viewportBreakpoint === MediaBreakpoint.MD) {
      setMdCssSelector(newCss, historyMode, throttleNumber);
    } else {
      setSmCssSelector(newCss, historyMode, throttleNumber);
    }
  };

  return {
    isRootViewportCss: isUsingDefaultCssControls,
    responsiveCss,
    setResponsiveCss,
    lgCss: css,
    mdCss,
    smCss,
  };
};
